import React, { createContext, useContext } from 'react';
import { createInstance } from '@optimizely/react-sdk';
import getConfig from 'next/config';

/**
 * Creates Optimizely client instance at module level (singleton pattern)
 */
const createOptimizelyClient = () => {
  // Only create on client-side to avoid SSR issues
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const nextConfig = getConfig();
    const sdkKey =
      nextConfig?.publicRuntimeConfig?.optimizelySDKKey ||
      process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY;

    const client = createInstance({ sdkKey });

    if (process.env.NODE_ENV === 'development') {
      console.log('Optimizely client initialized at module level');
    }

    return client;
  } catch (error) {
    console.error('Optimizely client creation failed:', error);
    return null;
  }
};

// Create instance once when module loads (singleton)
const optimizelyClient = createOptimizelyClient();

// Create the Optimizely context
const OptimizelyContext = createContext(null);

/**
 * Custom hook to access Optimizely client from context
 * @returns {Object} Optimizely client instance
 */
export const useOptimizely = () => {
  const context = useContext(OptimizelyContext);

  if (!context) {
    console.warn('useOptimizely must be used within a G4OptimizelyProvider');
    return null;
  }

  return context;
};

/**
 * G4 Optimizely Context Provider
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.Component} Provider component
 */
export const G4OptimizelyProvider = ({ children }) => {
  // Use pre-created module-level instance - no useMemo needed
  return (
    <OptimizelyContext.Provider value={optimizelyClient}>
      {children}
    </OptimizelyContext.Provider>
  );
};

export default G4OptimizelyProvider;
